import React, { useState, useMemo } from 'react';
import { Search, CheckSquare, Square, Users } from 'lucide-react';

const RecipientSelector = ({
  attendees = [],
  selectedIds = [],
  onChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customCount, setCustomCount] = useState('');
  const [customOffset, setCustomOffset] = useState('');

  // Filter attendees based on search term
  const filteredAttendees = useMemo(() => {
    if (!searchTerm.trim()) return attendees;
    const term = searchTerm.toLowerCase();
    return attendees.filter(a => 
      (a.full_name && a.full_name.toLowerCase().includes(term)) ||
      (a.email && a.email.toLowerCase().includes(term)) ||
      (a.phone && a.phone.toLowerCase().includes(term))
    );
  }, [attendees, searchTerm]);

  // Handle single check/uncheck
  const handleToggleAttendee = (id) => {
    const safeSelectedIds = selectedIds || [];
    const isSelected = safeSelectedIds.includes(id);
    let newSelection;
    if (isSelected) {
      newSelection = safeSelectedIds.filter(selectedId => selectedId !== id);
    } else {
      newSelection = [...safeSelectedIds, id];
    }
    onChange(newSelection);
  };

  // Safe fallback for null arrays from API
  const safeSelectedIds = selectedIds || [];

  // Handle select/deselect all currently filtered attendees
  const allFilteredSelected = useMemo(() => {
    if (filteredAttendees.length === 0) return false;
    return filteredAttendees.every(a => safeSelectedIds.includes(a.id));
  }, [filteredAttendees, safeSelectedIds]);

  const handleToggleSelectAll = () => {
    let newSelection;
    const filteredIds = filteredAttendees.map(a => a.id);
    const safeSelectedIds = selectedIds || [];
    if (allFilteredSelected) {
      // Uncheck all currently filtered
      newSelection = safeSelectedIds.filter(id => !filteredIds.includes(id));
    } else {
      // Check all currently filtered (add non-duplicates)
      newSelection = Array.from(newSet([...safeSelectedIds, ...filteredIds]));
    }
    onChange(newSelection);
  };

  // Helper utility for unique array values
  function newSet(arr) {
    return Array.from(new Set(arr));
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50/50 p-4 space-y-4">
      <div className="flex flex-col gap-3">
        {/* Recipient Count Summary */}
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-teal-600 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700">
            Selected: <strong className="text-teal-600 font-bold">{safeSelectedIds.length}</strong> of <span className="font-semibold">{attendees.length}</span> attendees
          </span>
        </div>

        {/* Custom subset count entry */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="custom-offset-input" className="text-sm text-gray-600 whitespace-nowrap">
              Skip first:
            </label>
            <input
              id="custom-offset-input"
              type="number"
              min="0"
              placeholder="0"
              value={customOffset}
              onChange={(e) => setCustomOffset(e.target.value)}
              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="custom-count-input" className="text-sm text-gray-600 whitespace-nowrap">
              Take next:
            </label>
            <input
              id="custom-count-input"
              type="number"
              min="1"
              placeholder="All"
              value={customCount}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || (parseInt(val) >= 1)) {
                  setCustomCount(val);
                }
              }}
              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              const offset = parseInt(customOffset) || 0;
              const count = parseInt(customCount);
              const available = filteredAttendees.slice(offset);
              const subset = isNaN(count) ? available : available.slice(0, count);
              onChange(subset.map(a => a.id));
            }}
            className="px-3 py-1 bg-teal-100 hover:bg-teal-200 text-teal-700 text-xs font-semibold rounded transition-colors whitespace-nowrap"
          >
            Auto-Select Checkboxes
          </button>
        </div>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search attendees by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
        />
      </div>

      {/* Select All Checkbox & Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded text-sm font-medium text-gray-700">
        <button
          type="button"
          onClick={handleToggleSelectAll}
          className="flex items-center gap-2 hover:text-teal-700 transition-colors"
        >
          {allFilteredSelected ? (
            <CheckSquare className="w-4 h-4 text-teal-600" />
          ) : (
            <Square className="w-4 h-4 text-gray-500" />
          )}
          <span>Toggle All Filtered ({filteredAttendees.length})</span>
        </button>
        <span className="text-xs text-gray-500">
          Showing {filteredAttendees.length} results
        </span>
      </div>

      {/* Attendee Checklist Container */}
      <div className="max-h-60 overflow-y-auto border border-gray-200 rounded bg-white divide-y divide-gray-100 scrollbar-thin">
        {filteredAttendees.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">
            No attendees found matching search terms.
          </div>
        ) : (
          filteredAttendees.map(attendee => {
            const isSelected = safeSelectedIds.includes(attendee.id);
            return (
              <div
                key={attendee.id}
                onClick={() => handleToggleAttendee(attendee.id)}
                className={`flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors ${
                  isSelected ? 'bg-teal-50/20' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {isSelected ? (
                    <CheckSquare className="w-4 h-4 text-teal-600 flex-shrink-0" />
                  ) : (
                    <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {attendee.full_name || 'Unnamed Attendee'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {attendee.email || 'No email'} {attendee.phone ? `| ${attendee.phone}` : ''}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-400 font-mono">
                  {attendee.registration_number || ''}
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {customCount && parseInt(customCount) > 0 && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
          Note: You have set a limit of {customCount}. The message will only be sent to the first {Math.min(parseInt(customCount), safeSelectedIds.length)} selected recipients.
        </div>
      )}
    </div>
  );
};

export default RecipientSelector;
